/**
 * `Messages` — what a component holds instead of a hardcoded sentence.
 * ==================================================================
 *
 * `Translator` (in `translate.ts`) answers *"what does this key say"* and is all the schema strings
 * need, because none of them takes a parameter. A blocking validation message does take parameters, so
 * component strings need a slightly wider interface: a key **plus** values, and a plural category
 * chosen from the values.
 *
 * The two share one staleness guard (`resolve.ts`) and one plural implementation (`message.ts`). They
 * are not two i18n layers; they are two call shapes over one.
 *
 * ─── THE INVARIANT THIS FILE OWES THE APP ──────────────────────────────────────────────────────
 *
 * **`m()` never returns an empty string and never returns a raw key for a declared message.** A blank
 * validation message beside a required field asks a technician to assert something about live
 * equipment with no idea what; a raw `ui.validate.required.answer` is only marginally better. Both are
 * worse than untranslated English, so both are unreachable for a declared key:
 *
 *   · a missing translation renders the English pattern (`resolve.ts`);
 *   · a stale translation renders the English pattern (`resolve.ts`);
 *   · a present-but-blank translation is treated as missing (`resolve.ts`);
 *   · a plural pattern always has an `other` form, because the type requires one (`message.ts`);
 *   · and if a translation somehow renders blank anyway, {@link makeMessages} falls back to the
 *     English render before returning — belt and braces, on the one path where the failure is silent.
 *
 * ─── DEFAULT IS ENGLISH, AND THAT IS WHY THIS LANDS SAFELY ─────────────────────────────────────
 *
 * `validate.ts`, `core/storage.ts` and `compose/ApiNotice.tsx` all take an **optional** `Messages`.
 * With none supplied they use {@link englishMessages}, whose output is byte-identical to the literals
 * they replaced. So every existing caller — including `src/ui/**` and `src/compose/screens.tsx`, which
 * another agent holds — keeps working untouched, and the locale is threaded through later by whoever
 * adds the `Translator` context to the shell. No component has to change on the same day as this file.
 */

import { SOURCE_LOCALE } from './locales.ts';
import type { Locale } from './locales.ts';
import type { LocaleCatalogue } from './catalog/types.ts';
import { formatMessage, isPluralPattern, patternForms } from './message.ts';
import type { MessageParams, MessagePattern, PluralCategory } from './message.ts';
import { resolvePattern, zeroCounts } from './resolve.ts';
import type { Resolution } from './resolve.ts';
import { UI_ENTRIES, uiSourceLookup } from './uiStrings.ts';

export interface MessageResolution {
  /** What to render. Never empty, never a raw key for a declared message. */
  readonly text: string;
  readonly resolution: Resolution;
  /** The English, rendered with the same parameters. Lets a review surface show both. */
  readonly source: string;
  /** Placeholders with no value. Empty for every message the app renders; a test pins that. */
  readonly unresolved: readonly string[];
  readonly category?: PluralCategory;
  /** `true` when a plural pattern had no form for the count's category and used `other`. */
  readonly usedOther?: boolean;
}

export interface MessagesOptions {
  /** Wrap every non-`translated` string in `‹…›`. QA sweeps only. Default `false`. */
  readonly markFallbacks?: boolean;
}

export interface Messages {
  readonly locale: Locale;
  /** The string to render. */
  m(key: string, params?: MessageParams): string;
  /** The string plus why it is that string, and what did not substitute. */
  describe(key: string, params?: MessageParams): MessageResolution;
  stats(): Readonly<Record<Resolution, number>>;
  /** Keys asked for that the catalogue does not declare. Should always be empty. */
  unknownKeys(): readonly string[];
  /**
   * Messages rendered with an unsubstituted placeholder, e.g. a `{count}` nobody passed.
   *
   * Not a debug aid. The schema workstream found `{intermediateAnchorCount}` reaching a technician
   * verbatim in maintenance help text; this is the list that turns that class of defect from
   * "somebody notices in the field" into an assertion.
   */
  unresolvedPlaceholders(): readonly { readonly key: string; readonly tokens: readonly string[] }[];
}

/** Render an English-or-translated pattern, with the empty-render backstop. */
function render(pattern: MessagePattern, english: MessagePattern, locale: string, params: MessageParams) {
  const formatted = formatMessage(pattern, locale, params);
  if (formatted.text.trim().length > 0) return formatted;
  // Unreachable through `resolvePattern`, which already refuses a blank translation. Kept because the
  // failure it guards is silent: a blank blocking message looks like a rendering bug, not a missing one.
  return formatMessage(english, locale, params);
}

/**
 * Build a `Messages` over one catalogue.
 *
 * `catalogue` is `null` for the source locale and for a locale whose chunk failed to load — both mean
 * "render the English", which is why they collapse to one argument rather than two code paths.
 */
export function makeMessages(
  locale: Locale,
  catalogue: LocaleCatalogue | null,
  options: MessagesOptions = {},
): Messages {
  const counts = zeroCounts();
  const unknown = new Set<string>();
  const unresolved = new Map<string, Set<string>>();
  const mark = options.markFallbacks === true;

  const describe = (key: string, params: MessageParams = {}): MessageResolution => {
    const resolved = resolvePattern({
      key,
      lookup: uiSourceLookup,
      locale,
      catalogue,
      mark,
      counts,
      unknown,
    });

    const formatted = render(resolved.pattern, resolved.source, locale, params);
    if (formatted.unresolved.length > 0) {
      const slot = unresolved.get(key) ?? new Set<string>();
      for (const token of formatted.unresolved) slot.add(token);
      unresolved.set(key, slot);
    }

    // The English, rendered the same way, so a review surface can show source and target side by side.
    const sourceText = formatMessage(resolved.source, SOURCE_LOCALE, params).text;

    return {
      text: formatted.text,
      resolution: resolved.resolution,
      source: sourceText,
      unresolved: formatted.unresolved,
      ...(formatted.category !== undefined ? { category: formatted.category } : {}),
      ...(formatted.usedOther !== undefined ? { usedOther: formatted.usedOther } : {}),
    };
  };

  return {
    locale,
    m: (key, params) => describe(key, params).text,
    describe,
    stats: () => ({ ...counts }),
    unknownKeys: () => [...unknown],
    unresolvedPlaceholders: () => [...unresolved].map(([key, tokens]) => ({ key, tokens: [...tokens] })),
  };
}

/**
 * The English `Messages`. Shared, because constructing one per validation call would allocate a Map per
 * keystroke on a form with live validation.
 *
 * ⚠️ It accumulates `stats()` for the lifetime of the process, which is fine for the counters it keeps
 * (all `source`) and is why {@link resetEnglishMessages} exists for tests that assert on them.
 */
let english: Messages | undefined;

export function englishMessages(): Messages {
  english ??= makeMessages(SOURCE_LOCALE, null);
  return english;
}

/** Test seam. Production has no reason to discard the shared English instance. */
export function resetEnglishMessages(): void {
  english = undefined;
}

/**
 * Coverage of a catalogue against the declared component strings.
 *
 * Three numbers rather than one percentage, for the same reason as `coverage()` in `translate.ts`: 90%
 * translated with 10% stale is a different release decision from 90% translated with 10% missing.
 */
export function uiCoverage(
  catalogue: LocaleCatalogue | null,
  keys: Iterable<string> = UI_ENTRIES.map((e) => e.key),
): { total: number; translated: number; missing: number; stale: number; missingPluralForms: number } {
  let total = 0;
  let translated = 0;
  let missing = 0;
  let stale = 0;
  let missingPluralForms = 0;

  for (const key of keys) {
    total += 1;
    const source = uiSourceLookup(key);
    const entry = catalogue?.entries[key];
    if (source === undefined || entry === undefined) {
      missing += 1;
      continue;
    }
    if (entry.sourceHash !== source.hash) {
      stale += 1;
      continue;
    }
    translated += 1;
    /*
     * A plural English translated as one sentence is legitimate (Japanese), so this is counted rather
     * than failed. It is the number a reviewer needs to ask "is that right for this language?".
     */
    if (isPluralPattern(source.pattern) && entry.forms === undefined && patternForms(source.pattern).length > 1) {
      missingPluralForms += 1;
    }
  }

  return { total, translated, missing, stale, missingPluralForms };
}
